import React, { useContext, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useParams } from 'react-router-dom'
import Input from 'components/Input'
import Textarea from 'components/Textarea'
import Select from 'components/Select'
import { SiteContext } from 'providers/site'
import { ProfileContext } from 'providers/profile'
import {
  getAssessmentParameterCheckFields
} from 'utils/getAssessmentParameterCheckFields'
import {
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_NOMINATED_ID,
  SITE_STATUS_PROPOSED_ID,
  SITE_STATUS_REVIEWED_BY_RFP_ID
} from 'constants/siteStatuses'
import { canViewSensitiveSpeciesAtSiteFullAccess } from 'utils/rolePermissions'
import MultiSelectionDropdown from 'components/multiSelectDropdown'
import MultiC2C3SelectionDropdown from 'components/multiSelectDropdown/multi'
import { NUMBER_OF_MATURE_INDIVIDUALS_ID } from 'constants/speciesParameterType'
import {
  EMPTY_ERROR,
  getFieldError
} from 'utils/speciesParameterSiteSpeciesDataHelpers'
import {
  getRelationshipFieldError,
  validateBestSiteAgainstBestGlobal,
  validateSiteEstimateOrder,
  withRelationshipError
} from 'validators/estimateRelationshipValidators'
import {
  createSpeciesParameterHandlers,
  formattedDerivationOptions,
  getIbaCriteriaEUOptions,
  buildCriteriaOptions,
  getNextCriteriaSelectionState,
  getIbaCriteriaOptionsByRegion
} from 'utils/speciesParameterSiteSpeciesDataContentHelpers'
import {
  LEGACY_ONLY_Value,
  getIdCriteria,
  arrIdCriteria,
  arrIdIbaCriteria
} from './constants'
import { useIBAAccess } from 'hooks/useIBAAccess'
import {
  SpeciesDetailsContainer,
  SpeciesDetailsItemWrapper,
  FullWidthSpecieEstimateItemWrapper,
  SpeciesDetailsIBAItemWrapper
} from '../style'

const bestEstimateFields = {
  bestSite: 'bestSite',
  bestGlobal: 'bestGlobal'
}

const siteEstimateFields = {
  min: 'minSite',
  best: 'bestSite',
  max: 'maxSite'
}

const requiredIbaFields = [
  'minRegional',
  'bestRegional',
  'maxRegional',
  'sourceRegionalData',
  'sourceOfEu',
  'euPopulation',
  'nationalPopulation',
  'sourceOfNational'
]

const getInitialCriteria = (parameter) => {
  const initialCriteria = getIdCriteria(parameter, arrIdCriteria)
  if (parameter.iba_only && !initialCriteria.includes(LEGACY_ONLY_Value)) {
    initialCriteria.push(LEGACY_ONLY_Value)
  }
  return initialCriteria
}

const isAssessmentFieldValid = (siteProposalFieldValidation, qualified, checkFields, fieldName) => {
  if (!siteProposalFieldValidation?.updated || !qualified || !checkFields) {
    return true
  }

  return checkFields[fieldName] !== false
}

const getShowBirdsDetails = ({ parameter, site, profile, species, hasSiteIBAAccessForBirds }) => {
  const isBirdSpecies = species.className === 'AVES'
  const hasSensitiveViewAccess =
    site.SiteStatusId === SITE_STATUS_PROPOSED_ID ||
    site.SiteStatusId === SITE_STATUS_NOMINATED_ID ||
    site.SiteStatusId === SITE_STATUS_REVIEWED_BY_RFP_ID ||
    canViewSensitiveSpeciesAtSiteFullAccess(site, profile.RoleId)
  const isAvesWithIbaAccess = hasSiteIBAAccessForBirds(site, species.className)
  const hasIbaFields = requiredIbaFields.every((field) => Object.keys(parameter).includes(field))
  return isBirdSpecies && hasIbaFields && (hasSensitiveViewAccess || isAvesWithIbaAccess)
}

const getMinYearError = ({ yearError, siteProposalFieldValidation, qualified, checkFields, error }) => {
  if (yearError.field === 'minYear') {
    return yearError
  }
  if (isAssessmentFieldValid(siteProposalFieldValidation, qualified, checkFields, 'earliestDataSetYear')) {
    return error.field === 'minYear' ? error : EMPTY_ERROR
  }
  return { field: 'minYear', error: 'pleaseCompleteThisField' }
}

const getIbaSecondStepOptions = ({ site, parameter, currentIbaSelection }) => {
  if (site.isEuCountry && parameter?.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID) {
    return getIbaCriteriaEUOptions(currentIbaSelection)
  }
  return getIbaCriteriaOptionsByRegion(site.isEuCountry)
}

const SpeciesParameterSiteSpeciesDataContent = ({
  parameter,
  siteSpecieParameterId,
  updateParameterList,
  species,
  qualified,
  assessmentParameter,
  siteBestGlobal,
  setSiteBestSite,
  siteNatureOfOccurance,
  setSiteNatureOfOccurance,
  siteJustification,
  setSiteJustification
}) => {
  const params = useParams()
  const { site, siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const disabled =
    !parameter?.SiteAssesmentParameterTypeId || site.SiteStatusId !== SITE_STATUS_DRAFT_ID || !isEditable || !qualified
  const { hasSiteIBAAccessForBirds } = useIBAAccess()
  const showBirdsDetails = getShowBirdsDetails({ parameter, site, profile, species, hasSiteIBAAccessForBirds })

  const [minSite, setMinSite] = useState(parameter.minSite || '')
  const [bestSite, setBestSite] = useState(parameter.bestSite || '')
  const [maxSite, setMaxSite] = useState(parameter.maxSite || '')
  const [source, setSource] = useState(parameter.sourceOfData || '')
  const [derivation, setDerivation] = useState(
    formattedDerivationOptions.find((item) => {
      return item.value === parameter.DerivationOfEstimateId
    }) || null
  )
  const [notes, setNotes] = useState(parameter.notesOnSiteDataForSpecies || '')
  const initialCriteria = getInitialCriteria(parameter)
  const [natureOfOccurrence, setNatureOfOccurrence] = useState(initialCriteria)
  // Local state for real-time criteria selection changes (before saving) - used for UI disabling logic
  const [currentCriteriaSelection, setCurrentCriteriaSelection] = useState(initialCriteria)
  const [ibaConditions, setIbaConditions] = useState(getIdCriteria(parameter, arrIdIbaCriteria))
  // Local state for real-time IBA selection changes (before saving)
  const [currentIbaSelection, setCurrentIbaSelection] = useState(getIdCriteria(parameter, arrIdIbaCriteria))
  const [justification, setJustification] = useState(parameter.justification || '')
  const [sourceHabitat, setSourceHabitat] = useState(parameter.globallyMostImportant5 || '')
  const [minYear, setMinYear] = useState(parameter.earliestDataSetYear ? parameter.earliestDataSetYear : '')
  const [maxYear, setMaxYear] = useState(parameter.latestDataSetYear ? parameter.latestDataSetYear : '')
  const [error, setError] = useState({ field: '', error: '' })
  const [yearError, setYearError] = useState({ field: '', error: '' })
  const [evidence, setEvidence] = useState(parameter.speciesPresenceEvidence || '')
  const [yearRecent, setYearRecent] = useState(parameter.yearPresenceEvidence || '')
  const [unitsNumber, setUnitsNumber] = useState(parameter.minReproductiveUnits || '')
  const [comprised, setComprised] = useState(parameter.ruCompromisedOf || '')
  const [sourceReproduc, setSourceReproduc] = useState(parameter.ruSource || '')

  const checkFields = getAssessmentParameterCheckFields(siteProposalFieldValidation, parameter.id)

  // Synchronize currentIbaSelection with ibaConditions when parameter changes
  useEffect(() => {
    setCurrentIbaSelection(ibaConditions)
  }, [ibaConditions])

  // Synchronize natureOfOccurrence and currentCriteriaSelection with parameter.iba_only when parameter changes
  useEffect(() => {
    const { shouldUpdate, nextValue } = getNextCriteriaSelectionState({ parameter, natureOfOccurrence })
    if (shouldUpdate) {
      setNatureOfOccurrence(nextValue)
      setCurrentCriteriaSelection(nextValue)
    }
  }, [parameter.iba_only, parameter.id])

  const {
    handleIbaSelectionChange,
    handleCriteriaSelectionChange,
    handleChangeCriteria,
    handleChangeIbaCriteria,
    handleChangeYearRecent,
    handleChangeUnitsNumber,
    handleChangeComprised,
    handleChangeSourceReproduc,
    handleChangeEvidence,
    handleChangeJustification,
    handleChangeSourceHabitat,
    handleChangeMinYear,
    handleChangeMaxYear,
    handleChangeMinSite,
    handleBlurMinSite,
    handleChangeBestSite,
    handleBlurBestSite,
    handleChangeMaxSite,
    handleBlurMaxSite,
    handleChangeSource,
    handleChangeDerivation,
    handleChangeNotes
  } = createSpeciesParameterHandlers({
    parameter,
    siteSpecieParameterId,
    siteHistoryId: params.siteHistoryId,
    species,
    updateParameterList,
    assessmentParameter,
    siteBestGlobal,
    minSite,
    bestSite,
    maxSite,
    minYear,
    maxYear,
    setCurrentIbaSelection,
    setCurrentCriteriaSelection,
    setNatureOfOccurrence,
    setSiteNatureOfOccurance,
    setSiteJustification,
    siteNatureOfOccurance,
    siteJustification,
    setYearRecent,
    setUnitsNumber,
    setComprised,
    setSourceReproduc,
    setEvidence,
    setJustification,
    setSourceHabitat,
    setMinYear,
    setMaxYear,
    setYearError,
    setMinSite,
    setBestSite,
    setSiteBestSite,
    setMaxSite,
    setError,
    setSource,
    setDerivation,
    setNotes,
    setIbaConditions
  })

  const shouldValidateField = (fieldName) =>
    isAssessmentFieldValid(siteProposalFieldValidation, qualified, checkFields, fieldName)

  function renderCriteriaOptions() {
    return buildCriteriaOptions(currentCriteriaSelection, assessmentParameter, siteBestGlobal)
  }

  const minYearError = getMinYearError({ yearError, siteProposalFieldValidation, qualified, checkFields, error })
  const ibaSecondStepOptions = getIbaSecondStepOptions({ site, parameter, currentIbaSelection })
  // Re-evaluated on every render, so editing minSite, bestSite, maxSite here or bestGlobal in the global
  // data accordion flags all affected fields immediately.
  const siteEstimateRelationshipErrors = {
    ...validateBestSiteAgainstBestGlobal(bestSite, siteBestGlobal, bestEstimateFields),
    ...validateSiteEstimateOrder(minSite, bestSite, maxSite, siteEstimateFields)
  }
  const minSiteError = getRelationshipFieldError(siteEstimateRelationshipErrors, 'minSite')
  const maxSiteError = getRelationshipFieldError(siteEstimateRelationshipErrors, 'maxSite')
  const bestSiteError = withRelationshipError(
    siteEstimateRelationshipErrors,
    'bestSite',
    getFieldError(shouldValidateField('bestSite'), error, 'bestSite')
  )
  const maxYearError = yearError.field === 'maxYear' ? yearError : EMPTY_ERROR
  const sourceError = shouldValidateField('sourceOfData')
    ? EMPTY_ERROR
    : { field: 'sourceOfData', error: 'pleaseCompleteThisField' }
  const derivationError = shouldValidateField('DerivationOfEstimateId')
    ? EMPTY_ERROR
    : { field: 'DerivationOfEstimateId', error: 'pleaseCompleteThisField' }
  const evidenceError = shouldValidateField('speciesPresenceEvidence')
    ? EMPTY_ERROR
    : { field: 'evidenceThatSpeciesArePresentAtTheSite', error: 'pleaseCompleteThisField' }
  const yearPresenceError = shouldValidateField('yearPresenceEvidence')
    ? EMPTY_ERROR
    : { field: 'mostRecentYearForEvidenceOfPresence', error: 'pleaseCompleteThisField' }
  const natureOfOccurrenceError = parameter.iba_only === true || shouldValidateField('natureOfOccurence')
    ? EMPTY_ERROR
    : { field: 'natureOfOccurrence', error: 'pleaseCompleteThisField' }
  const justificationError = shouldValidateField('justification')
    ? EMPTY_ERROR
    : { field: 'justificationThatTheSpeciesAggregated', error: 'pleaseCompleteThisField' }

  return (
    <SpeciesDetailsContainer noPadding={true}>
      <SpeciesDetailsItemWrapper>
        <Input
          value={minYear}
          onChange={handleChangeMinYear}
          placeholder="minYearPlaceholder"
          label="minYear"
          field="minYear"
          infoTooltip="minYearTooltip"
          zIndex="1070"
          disabled={disabled}
          errorMarginTop="8px"
          error={minYearError}
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={maxYear}
          onChange={handleChangeMaxYear}
          placeholder="maxYearPlaceholder"
          label="maxYear"
          field="maxYear"
          infoTooltip="maxYearTooltip"
          zIndex="1070"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={true}
          error={maxYearError}
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={minSite}
          onChange={handleChangeMinSite}
          onBlur={handleBlurMinSite}
          placeholder="enterSpeciesMinSiteData"
          label="minSite"
          field="minSite"
          infoTooltip="minSiteHint"
          zIndex="1070"
          isOptional={true}
          disabled={disabled}
          errorMarginTop="8px"
          error={minSiteError}
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={bestSite}
          onChange={handleChangeBestSite}
          onBlur={handleBlurBestSite}
          placeholder="enterSpeciesBestSiteData"
          label="bestSite"
          field="bestSite"
          infoTooltip="bestSiteHint"
          zIndex="1070"
          isOptional={siteNatureOfOccurance}
          disabled={disabled}
          errorMarginTop="8px"
          error={bestSiteError}
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={maxSite}
          onChange={handleChangeMaxSite}
          onBlur={handleBlurMaxSite}
          placeholder="enterSpeciesMaxSiteData"
          label="maxSite"
          field="maxSite"
          infoTooltip="maxSiteHint"
          zIndex="1070"
          disabled={disabled}
          isOptional={true}
          errorMarginTop="8px"
          error={maxSiteError}
        />
      </SpeciesDetailsItemWrapper>
      <div style={{ width: '100%' }}>
        <Textarea
          value={source}
          onChange={handleChangeSource}
          placeholder="enterSpeciesSiteSourceData"
          label="source"
          field="sourceOfData"
          infoTooltip="siteSourceHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          error={sourceError}
        />
      </div>
      <SpeciesDetailsItemWrapper>
        <Select
          value={derivation}
          onChange={handleChangeDerivation}
          options={formattedDerivationOptions}
          placeholder="derivationOfEstimate"
          label="derivation"
          field="DerivationOfEstimateId"
          infoTooltip="derivationHint"
          zIndex="1070"
          disabled={disabled}
          errorMarginTop="8px"
          error={derivationError}
        />
      </SpeciesDetailsItemWrapper>
      <div style={{ width: '100%' }}>
        <Textarea
          value={evidence}
          onChange={handleChangeEvidence}
          placeholder="evidenceTheSpeciesIsPresentAtTheSite"
          label="evidence"
          field="evidenceThatSpeciesArePresentAtTheSite"
          infoTooltip="evidenceHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          error={evidenceError}
        />
      </div>
      <SpeciesDetailsItemWrapper>
        <Input
          value={yearRecent}
          onChange={handleChangeYearRecent}
          placeholder="yearForWitchThereIsEvidence"
          label="yearPresence"
          field="mostRecentYearForEvidenceOfPresence"
          infoTooltip="yearSpeciesDetailsHint"
          zIndex="1070"
          disabled={disabled}
          errorMarginTop="8px"
          error={yearPresenceError}
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={unitsNumber}
          onChange={handleChangeUnitsNumber}
          placeholder="minNumberOfReproductiveUnitsRuAtSite"
          label="unitsNumber"
          field="minNumberOfReproductiveUnits"
          infoTooltip="unitsNumberHint"
          zIndex="1070"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={true}
        />
      </SpeciesDetailsItemWrapper>

      <div style={{ width: '100%' }}>
        <Textarea
          value={sourceReproduc}
          onChange={handleChangeSourceReproduc}
          placeholder="sourceOfReproductiveUnitDataIncludingYear"
          label="sourceOfRuData"
          field="sourceOfReproductiveUnit"
          infoTooltip="speciesDetailsSourceHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={true}
        />
      </div>
      <div style={{ width: '100%' }}>
        <Textarea
          value={comprised}
          onChange={handleChangeComprised}
          placeholder="whatAre10RusComprisedOf"
          label="comprised"
          field="whatAre10RUcomprisedOf"
          infoTooltip="comprisedHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={true}
        />
      </div>
      <SpeciesDetailsItemWrapper>
        <MultiSelectionDropdown
          marginBottom="24px"
          marginTop="16px"
          label="natureOfOccurrence"
          field="natureOfOccurrence"
          secondStepOptions={renderCriteriaOptions()}
          onSelectionChange={handleCriteriaSelectionChange}
          value={natureOfOccurrence}
          saveChanges={handleChangeCriteria}
          infoTooltip="aggregationCriteriaHint"
          zIndex="1070"
          errorMarginTop="8px"
          error={natureOfOccurrenceError}
        />
      </SpeciesDetailsItemWrapper>
      <div style={{ width: '100%' }}>
        <Textarea
          value={justification}
          onChange={handleChangeJustification}
          placeholder="justificationThatTheSpeciesIsAggregated"
          label="justification"
          field="justificationThatTheSpeciesAggregated"
          infoTooltip="largestAggregationHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={siteJustification}
          error={justificationError}
        />
      </div>

      <div style={{ width: '100%' }}>
        <Textarea
          value={sourceHabitat}
          onChange={handleChangeSourceHabitat}
          placeholder="globallyMostImportant5OccupiedHabitat"
          label="source"
          field="sourceOfBeingGloballyMostImportant5PercentOccupiedHabitat"
          infoTooltip="sourceHabitatHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={true}
        />
      </div>

      {showBirdsDetails && (
        <FullWidthSpecieEstimateItemWrapper>
          <SpeciesDetailsIBAItemWrapper>
            <MultiC2C3SelectionDropdown
              dataTestId="ibaRelated"
              marginBottom="24px"
              marginTop="16px"
              label="ibaRelated"
              field="ibaRelated"
              onSelectionChange={handleIbaSelectionChange}
              secondStepOptions={ibaSecondStepOptions}
              value={ibaConditions}
              saveChanges={handleChangeIbaCriteria}
              infoTooltip="ibaRelatedCriteriaHint"
              zIndex="1070"
              isOptional={true}
              errorMarginTop="8px"
            />
          </SpeciesDetailsIBAItemWrapper>
        </FullWidthSpecieEstimateItemWrapper>
      )}

      <FullWidthSpecieEstimateItemWrapper>
        <Textarea
          value={notes}
          onChange={handleChangeNotes}
          placeholder="enterNotesOnSpeciesSiteData"
          label="noteLabel"
          infoTooltip="siteNoteHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          isOptional={true}
        />
      </FullWidthSpecieEstimateItemWrapper>
    </SpeciesDetailsContainer>
  )
}

export default SpeciesParameterSiteSpeciesDataContent

SpeciesParameterSiteSpeciesDataContent.propTypes = {
  parameter: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    SiteAssesmentParameterTypeId: PropTypes.number,
    iba_only: PropTypes.bool,
    className: PropTypes.string,
    minSite: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    bestSite: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    maxSite: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sourceOfData: PropTypes.string,
    notesOnSiteDataForSpecies: PropTypes.string,
    minReproductiveUnits: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    speciesPresenceEvidence: PropTypes.string,
    DerivationOfEstimateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    justification: PropTypes.string,
    globallyMostImportant5: PropTypes.string,
    earliestDataSetYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    latestDataSetYear: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    yearPresenceEvidence: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ruCompromisedOf: PropTypes.string,
    ruSource: PropTypes.string
  }).isRequired,
  siteSpecieParameterId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  updateParameterList: PropTypes.func.isRequired,
  species: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    historyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    className: PropTypes.string
  }).isRequired,
  qualified: PropTypes.bool,
  assessmentParameter: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  siteBestGlobal: PropTypes.oneOfType([PropTypes.number, PropTypes.string, PropTypes.bool]),
  setSiteBestSite: PropTypes.func,
  siteNatureOfOccurance: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  setSiteNatureOfOccurance: PropTypes.func.isRequired,
  siteJustification: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  setSiteJustification: PropTypes.func.isRequired
}

SpeciesParameterSiteSpeciesDataContent.defaultProps = {
  qualified: false,
  assessmentParameter: {},
  siteBestGlobal: '',
  setSiteBestSite: undefined,
  siteNatureOfOccurance: false,
  siteJustification: false
}
